import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  DocumentReference, 
  Timestamp,
  deleteDoc,
  collectionGroup
} from 'firebase/firestore';
import { db } from './config';
import { Climber } from '../../models/user/Climber';
import { GradeSystem } from '../../models/climbing/Grade';

const CLIMBERS_COLLECTION = 'climbers';

// Create a new climber document in Firestore
export const createClimber = async (firebaseId: string, username?: string, name?: string, language?: string): Promise<Climber> => {
  try {
    const climberRef = doc(db, CLIMBERS_COLLECTION, firebaseId);
    
    // Default values
    const newClimber = new Climber(firebaseId, {
      username: username || undefined,
      name: name || undefined,
      language: language || 'EN', // Default to English if not specified
      boulderGradeSystem: GradeSystem.FRENCH, // Default to French system
      ropeGradeSystem: GradeSystem.FRENCH,    // Default to French system
      lastLogin: Date.now(),
      bananas: 0,
      isBlocked: false,
      role: 'user',
      subscribed: 'o', // Default to 'o' for newsletter prompt
      gymMemberships: {}
    });
    
    // Remove undefined values before saving to Firestore
    const firestoreData = newClimber.toFirestore();
    const cleanedData = removeUndefinedValues(firestoreData);
    
    await setDoc(climberRef, cleanedData);
    return newClimber;
  } catch (error) {
    console.error('Error creating climber:', error);
    throw error;
  }
};

// Get a climber by Firebase ID
export const getClimber = async (firebaseId: string): Promise<Climber | null> => {
  try {
    const climberRef = doc(db, CLIMBERS_COLLECTION, firebaseId);
    const climberDoc = await getDoc(climberRef);
    
    if (climberDoc.exists()) {
      return Climber.fromFirestore({ ...climberDoc.data(), firebaseId });
    }
    
    return null;
  } catch (error) {
    console.error('Error getting climber:', error);
    throw error;
  }
};

// Get all climbers (for admin dashboard)
export const getAllClimbers = async (): Promise<Climber[]> => {
  try {
    const climbersCollection = collection(db, CLIMBERS_COLLECTION);
    const climbersSnapshot = await getDocs(climbersCollection);
    
    const climbers: Climber[] = [];
    
    climbersSnapshot.forEach(doc => {
      const data = { ...doc.data(), firebaseId: doc.id };
      climbers.push(Climber.fromFirestore(data));
    });
    
    return climbers;
  } catch (error) {
    console.error('Error getting all climbers:', error);
    throw error;
  }
};

// Update a climber document
export const updateClimber = async (climber: Climber): Promise<void> => {
  try {
    const climberRef = doc(db, CLIMBERS_COLLECTION, climber.firebaseId);
    const firestoreData = climber.toFirestore();
    const cleanedData = removeUndefinedValues(firestoreData);
    await updateDoc(climberRef, cleanedData);
  } catch (error) {
    console.error('Error updating climber:', error);
    throw error;
  }
};

// Helper function to remove undefined values from objects before sending to Firestore
const removeUndefinedValues = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    
    // Skip undefined values
    if (value === undefined) return;
    
    // For nested objects (excluding arrays), recursively remove undefined values
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = removeUndefinedValues(value);
    } else {
      // For arrays, primitive values and null
      result[key] = value;
    }
  });
  
  return result;
};

// Update specific climber fields
export const updateClimberFields = async (
  firebaseId: string, 
  fields: Partial<Omit<Climber, 'firebaseId' | 'climbs'>>
): Promise<void> => {
  try {
    const climberRef = doc(db, CLIMBERS_COLLECTION, firebaseId);
    const cleanedFields = removeUndefinedValues(fields);
    await updateDoc(climberRef, cleanedFields);
  } catch (error) {
    console.error('Error updating climber fields:', error);
    throw error;
  }
};

// Update grade system preferences
export const updateGradeSystemPreferences = async (
  firebaseId: string,
  boulderGradeSystem?: GradeSystem,
  ropeGradeSystem?: GradeSystem
): Promise<void> => {
  try {
    const updates: Record<string, any> = {};
    
    if (boulderGradeSystem) {
      updates.boulderGradeSystem = boulderGradeSystem;
    }
    
    if (ropeGradeSystem) {
      updates.ropeGradeSystem = ropeGradeSystem;
    }
    
    if (Object.keys(updates).length > 0) {
      const climberRef = doc(db, CLIMBERS_COLLECTION, firebaseId);
      const cleanedUpdates = removeUndefinedValues(updates);
      await updateDoc(climberRef, cleanedUpdates);
    }
  } catch (error) {
    console.error('Error updating grade system preferences:', error);
    throw error;
  }
};

// Update the last login timestamp
export const updateLastLogin = async (firebaseId: string): Promise<void> => {
  try {
    const climberRef = doc(db, CLIMBERS_COLLECTION, firebaseId);
    await updateDoc(climberRef, {
      lastLogin: Date.now()
    });
  } catch (error) {
    console.error('Error updating last login:', error);
    throw error;
  }
};

// Add or update a gym membership
export const updateGymMembership = async (
  firebaseId: string,
  gymId: string,
  role: string = 'climber'
): Promise<void> => {
  try {
    const climber = await getClimber(firebaseId);
    
    if (!climber) {
      throw new Error('Climber not found');
    }
    
    // Update the gym membership
    climber.updateGymMembership(gymId, role);
    
    // Save to Firestore
    const climberRef = doc(db, CLIMBERS_COLLECTION, firebaseId);
    await updateDoc(climberRef, {
      gymMemberships: climber.gymMemberships
    });
  } catch (error) {
    console.error('Error updating gym membership:', error);
    throw error;
  }
};

// Remove a gym membership
export const removeGymMembership = async (
  firebaseId: string,
  gymId: string
): Promise<void> => {
  try {
    const climber = await getClimber(firebaseId);
    
    if (!climber) {
      throw new Error('Climber not found');
    }
    
    // Remove the gym membership
    climber.removeGymMembership(gymId);
    
    // Save to Firestore
    const climberRef = doc(db, CLIMBERS_COLLECTION, firebaseId);
    await updateDoc(climberRef, {
      gymMemberships: climber.gymMemberships
    });
  } catch (error) {
    console.error('Error removing gym membership:', error);
    throw error;
  }
};

// Check if a climber is a member of a specific gym
export const isClimberGymMember = async (
  firebaseId: string,
  gymId: string
): Promise<boolean> => {
  try {
    const climber = await getClimber(firebaseId);
    
    if (!climber) {
      return false;
    }
    
    return climber.isMemberOfGym(gymId);
  } catch (error) {
    console.error('Error checking gym membership:', error);
    return false;
  }
};

// Get a climber's role in a specific gym
export const getClimberGymRole = async (
  firebaseId: string,
  gymId: string
): Promise<string | null> => {
  try {
    const climber = await getClimber(firebaseId);
    
    if (!climber) {
      return null;
    }
    
    return climber.getRoleInGym(gymId);
  } catch (error) {
    console.error('Error getting gym role:', error);
    return null;
  }
};

/**
 * Delete a climber document and all their archived climbs
 */
export const deleteClimber = async (climberId: string): Promise<void> => {
  try {
    // Delete the main climber document
    const climberRef = doc(db, CLIMBERS_COLLECTION, climberId);
    
    // Get the archived climbs collection for this climber
    const archivedClimbsCollectionRef = collection(db, CLIMBERS_COLLECTION, climberId, 'archived_climbs');
    const archivedClimbsSnapshot = await getDocs(archivedClimbsCollectionRef);
    
    // Delete all archived climb documents
    const deletePromises: Promise<void>[] = [];
    archivedClimbsSnapshot.forEach((doc) => {
      deletePromises.push(deleteDoc(doc.ref));
    });
    
    // Wait for all archived climbs to be deleted
    await Promise.all(deletePromises);
    
    // Delete the main climber document last
    await deleteDoc(climberRef);
    
    console.log(`Deleted climber ${climberId} and all their archived climbs`);
  } catch (error) {
    console.error('Error deleting climber:', error);
    throw error;
  }
};

/**
 * Remove a climber from all gym rankings
 */
export const removeClimberFromAllRankings = async (climberId: string): Promise<void> => {
  try {
    // Get all gyms' ranking documents
    const rankingsQuery = collectionGroup(db, 'rankings');
    const rankingsSnapshot = await getDocs(rankingsQuery);
    
    if (rankingsSnapshot.empty) {
      console.log('No ranking documents found');
      return;
    }
    
    const updatePromises: Promise<void>[] = [];
    
    rankingsSnapshot.forEach((rankingDoc) => {
      const rankingData = rankingDoc.data();
      const rankings = rankingData.rankings || [];
      
      // Filter out entries for this climber
      const updatedRankings = rankings.filter(
        (entry: { climberId: string }) => entry.climberId !== climberId
      );
      
      // Only update if we actually removed something
      if (updatedRankings.length !== rankings.length) {
        console.log(`Removing climber ${climberId} from ranking ${rankingDoc.ref.path}`);
        updatePromises.push(setDoc(rankingDoc.ref, { rankings: updatedRankings }, { merge: true }));
      }
    });
    
    // Wait for all updates to complete
    await Promise.all(updatePromises);
    
    console.log(`Removed climber ${climberId} from all gym rankings`);
  } catch (error) {
    console.error('Error removing climber from rankings:', error);
    throw error;
  }
};
